import { z } from 'zod';

export const RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX = 25;
/** Architectural cap; the persisted setting remains the runtime batch owner. */
export const RELATION_DEAD_REPAIR_BATCH_SIZE_MAX = 25;
const positivePostgresIntegerSchema = z.number().int().positive().max(2_147_483_647);

export const syncWorkerSettingsValueSchema = z.object({
  workerRuntimeEnabled: z.boolean(),
  apiEnqueueEnabled: z.boolean(),
  syncPipelineEnabled: z.boolean(),
  deleteExecutionEnabled: z.boolean(),
  deleteBacklogCron: z.string(),
  deleteBacklogLeaseMs: z.number(),
  deleteBacklogMaxAttempts: z.number(),
  deleteBacklogBaseBackoffSeconds: z.number(),
  deleteBacklogMaxBackoffSeconds: z.number(),
  deleteVerificationTimeoutMs: z.number(),
  deleteBacklogMaxRowsPerRun: z.number(),
  deleteBacklogClaimBatchSize: z.number(),
  deleteBacklogMaxDurationMs: z.number(),
  deleteWebhookInboxMaxRecordsPerRun: z.number(),
  deleteWebhookInboxMaxIdsPerRecord: z.number(),
  createUpdateWebhookInboxMaxRecordsPerRun: z.number(),
  deleteWebhookInboxBulkAckMinRecords: z.number(),
  createUpdateWebhookInboxBulkAckMinRecords: z.number(),
  snapshotExecutionEnabled: z.boolean(),
  snapshotSupervisorIntervalSeconds: z.number(),
  snapshotJobsFreshnessSlaSeconds: z.number(),
  snapshotDataTypesFreshnessSlaSeconds: z.number(),
  snapshotRelationQueueFreshnessSlaSeconds: z.number(),
  snapshotLeaseWindowSeconds: z.number(),
  snapshotHeartbeatIntervalSeconds: z.number(),
  globalDeltaCron: z.string(),
  inventoryPostgresCountEnabled: z.boolean(),
  inventoryPostgresCountCron: z.string(),
  inventoryBubbleCountEnabled: z.boolean(),
  inventoryBubbleCountCron: z.string(),
  analyticsGearEnabled: z.boolean(),
  inventoryCountsStaleWarningThresholdMinutes: z.number(),
  syncMatchFreshnessHours: z.number(),
  columnParityRefreshEnabled: z.boolean(),
  columnParityRefreshCron: z.string(),
  columnParityRefreshMaxAttempts: z.number(),
  columnParityRefreshBaseBackoffSeconds: z.number(),
  columnParityRefreshMaxBackoffSeconds: z.number(),
  dataValidationEnabled: z.boolean(),
  dataValidationMaxSampleLimit: z.number(),
  dataValidationDefaultChunkSize: z.number(),
  dataValidationBubbleRequestConcurrency: z.number(),
  dataValidationBubbleRequestsPerMinute: z.number(),
  dataValidationReportRetentionDays: z.number(),
  dataValidationFullScanEnabled: z.boolean(),
  dataValidationLeaseMs: z.number(),
  dataValidationCleanupBatchSize: z.number(),
  dataValidationMaxReportBytes: z.number(),
  dataValidationScheduleEnabled: z.boolean(),
  dataValidationScheduleCron: z.string(),
  dataValidationLookbackDays: z.number(),
  dataValidationSafetyLagDays: z.number(),
  dataValidationRepairWaitMinutes: z.number(),
  dataValidationMaxRowsPerTable: z.number(),
  dataValidationDirtyRecheckAttempts: z.number(),
  dataValidationMaxTablesPerCampaign: z.number(),
  dataValidationSnapshotRetentionDays: z.number(),
  dataValidationDirtySnapshotRetentionDays: z.number(),
  dataValidationRawReportRetentionDays: z.number(),
  dataValidationFinalReportRetentionDays: z.number(),
  dataValidationFreshnessDays: z.number(),
  refreshBatchSize: z.number(),
  refreshBatchDelayMs: z.number(),
  relationRefreshCron: z.string(),
  relationRefreshBatchLimit: z.number(),
  relationRefreshLeaseMs: z.number(),
  relationRefreshLockTimeoutMs: z.number(),
  relationRefreshStatementTimeoutMs: z.number(),
  relationRefreshIdleInTxTimeoutMs: z.number(),
  relationRefreshEnabled: z.boolean(),
  relationRefreshBulkEnabled: z.boolean(),
  relationRefreshBulkCron: z.string(),
  relationRefreshBulkMicrobatchSize: z.number().int().min(1),
  relationRefreshBulkTimeBudgetMs: z.number(),
  relationRefreshBulkLeaseMs: z.number(),
  relationSnapshotExactRecountEnabled: z.boolean(),
  relationSnapshotExactRecountCron: z.string(),
  relationSnapshotExactRecountStatementTimeoutMs: z.number(),
  relationSnapshotExactRecountMaxTablesPerRun: z.number(),
  exactIdSyncEnabled: z.boolean(),
  exactIdTerminalRetentionDays: z.number(),
  exactIdNotFoundClassifierCron: z.string(),
  exactIdNotFoundClassifierSafetyLagMinutes: positivePostgresIntegerSchema,
  retentionCleanupIntervalMinutes: positivePostgresIntegerSchema,
  syncHistoryCleanupBatchSize: positivePostgresIntegerSchema,
  workerIncidentsCleanupBatchSize: positivePostgresIntegerSchema,
  syncTableQueueCleanupBatchSize: positivePostgresIntegerSchema,
  syncExactIdQueueCleanupBatchSize: positivePostgresIntegerSchema,
  jobRunsCompletedCleanupBatchSize: positivePostgresIntegerSchema,
  jobRunsFailedCancelledCleanupBatchSize: positivePostgresIntegerSchema,
  syncCountAuditLeaseCleanupBatchSize: positivePostgresIntegerSchema,
  relationDoneRowsCleanupBatchSize: positivePostgresIntegerSchema,
  deletedRelationCleanupQueueBatchSize: positivePostgresIntegerSchema,
  syncReconciliationRunsCleanupBatchSize: positivePostgresIntegerSchema,
  syncEntityReviewBacklogCleanupBatchSize: positivePostgresIntegerSchema,
  bubbleMutationSuccessCleanupBatchSize: positivePostgresIntegerSchema,
  bubbleMutationNonSuccessRedactionBatchSize: positivePostgresIntegerSchema,
  webhookInboxLedgerCleanupBatchSize: positivePostgresIntegerSchema,
  syncTableQueueTotalExecutionsLimit: z.number().int().min(1),
  syncExactIdQueueTotalExecutionsLimit: z.number().int().min(1),
  relationRefreshQueueTotalExecutionsLimit: z.number().int().min(1),
  mediaOwnerSyncQueueTotalExecutionsLimit: z.number().int().min(1),
  mediaImportQueueTotalExecutionsLimit: z.number().int().min(1),
  webhookDeltaFallbackEnabled: z.boolean(),
  relationDeadRepairEnabled: z.boolean(),
  relationDeadRepairBatchSize: z.number(),
  relationDeadRepairTimeBudgetMs: z.number(),
  relationDeadRepairBubbleRateLimitPerMinute: z.number(),
  relationDeadRepairLeaseMs: z.number(),
  relationDeadRepairMaxAttempts: z.number(),
  relationDeadRepairRetentionDays: z.number(),
  relationDeadRepairRootCron: z.string(),
  relationDeadRepairContinuationDelayMs: z.number(),
  deletedRelationCleanupEnabled: z.boolean(),
  deletedRelationCleanupBatchSize: z.number(),
  deletedRelationCleanupRateLimitPerMinute: z.number(),
  deletedRelationCleanupLeaseMs: z.number(),
  deletedRelationCleanupMaxAttempts: z.number(),
  deletedRelationCleanupRetentionDays: z.number(),
  syncReconciliationMaxIdsPerBubbleRecheckBatch: z.number(),
  syncReconciliationMaxConflictRecheckBatchSize: z.number(),
  syncReconciliationMaxPagesPerBucket: z.number(),
  syncReconciliationMaxBucketsPerRun: z.number(),
  syncReconciliationMaxActionsPerRun: z.number(),
  syncReconciliationMaxDeleteActionsPerRun: z.number(),
  syncReconciliationMaxExactIdActionsPerRun: z.number(),
  syncReconciliationMaxDurationMs: z.number(),
  syncReconciliationSafetyLagMinutes: z.number(),
  syncReconciliationBucketMaxAttempts: z.number(),
  syncReconciliationActionRetentionDays: z.number(),
  syncReconciliationTierMaxRunsPerTick: z.number(),
  syncReconciliationTierDefaultLookbackHours: z.number(),
  syncSymmetricReconciliationEnabled: z.boolean(),
  countMismatchDelayedRecountDelayMinutes: z.number(),
  mirrorDeltaPageSize: z.number(),
  mirrorDeltaBatchSize: z.number(),
  mirrorDeltaWorkBatchSize: z.number(),
  mirrorDeltaPollIntervalSeconds: z.number(),
  maxActiveSyncTables: z.number(),
  exactIdMaxActiveTables: z.number(),
  deltaMaxActiveTables: z.number(),
  standardMainChunkSize: z.number(),
  heavyRelationMainChunkSize: z.number(),
  standardPostprocessMicrobatchSize: z.number(),
  heavyRelationPostprocessMicrobatchSize: z.number(),
  postprocessLeaseMs: z.number(),
  postprocessTimeBudgetMs: z.number(),
  mediaImportSmallConcurrency: z.number(),
  mediaImportLargeConcurrency: z.number(),
  mediaImportClaimBatchSize: z.number(),
  mediaImportSchedulerEnabled: z.boolean(),
  mediaImportLargeThresholdBytes: z.number(),
  mediaImportDbPressureBreakerEnabled: z.boolean(),
  mediaImportDbPressureThresholdMs: z.number(),
  mediaImportDbPressureCooldownSeconds: z.number(),
  mediaImportDegradedSmallConcurrency: z.number(),
  mediaImportDegradedLargeConcurrency: z.number(),
  mediaImportPreflightEnabled: z.boolean(),
  mediaImportPreflightConcurrency: z.number(),
  mediaImportPreflightTimeoutMs: z.number(),
  mediaOwnerSyncSchedulerEnabled: z.boolean(),
  mediaOwnerMissingReconcileIntervalMinutes: z.number().int().positive().max(35_791),
  mediaOwnerSyncClaimBatchSize: z.number(),
  mediaOwnerSyncProcessConcurrency: z.number(),
  mediaOwnerSyncMaxActive: z.number(),
  mediaOwnerSyncTickTimeBudgetMs: z.number(),
  mediaOwnerSyncDbPressureBreakerEnabled: z.boolean(),
  mediaOwnerSyncClaimDbPressureThresholdMs: z.number(),
  testModeEnabled: z.boolean(),
  testModeBubbleKey: z.string().nullable(),
  testModeRecordLimit: z.number(),
});

export const syncWorkerSettingsClampMetaSchema = z.object({
  field: z.string(),
  requested: z.number(),
  effective: z.number(),
  reason: z.enum([
    'env_ceiling',
    'delete_backlog_lease_runtime_floor',
    'delete_verification_timeout_runtime_cap',
    'delete_verification_timeout_before_lease',
    'delete_backlog_max_duration_before_lease',
    'snapshot_heartbeat_before_lease',
    'postprocess_time_budget_before_lease',
    'relation_refresh_bulk_hard_cap',
    'relation_refresh_bulk_lease_after_time_budget',
    'media_import_capacity_cap',
    'media_owner_sync_capacity_cap',
  ]),
  envKey: z.string().optional(),
});

export const syncWorkerSettingsMetaSchema = z.object({
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
  clampedFields: z.record(z.string(), syncWorkerSettingsClampMetaSchema),
  snapshotExecution: z.object({
    requestedEnabled: z.boolean(),
    workerRuntimeEnabled: z.boolean(),
    effectiveEnabled: z.boolean(),
    reason: z.enum(['global_worker_execution_disabled', 'snapshot_execution_disabled']).nullable(),
  }),
});

export const syncWorkerSettingsEnvelopeSchema = z.object({
  requested: syncWorkerSettingsValueSchema,
  effective: syncWorkerSettingsValueSchema,
  meta: syncWorkerSettingsMetaSchema,
});

export const syncWorkerSettingsResponseSchema = z.object({
  environment: z.string(),
  settings: syncWorkerSettingsEnvelopeSchema,
  slackReport: z
    .object({
      available: z.boolean(),
      schedule: z.object({
        cron: z.literal('0 9 * * *'),
        timezone: z.literal('Europe/Moscow'),
      }),
      lastDelivery: z
        .object({
          status: z.enum(['sent', 'failed']),
          messageTs: z.string().nullable(),
          deliveredAt: z.iso.datetime(),
        })
        .nullable(),
    })
    .optional(),
});

export const syncWorkerSlackReportResponseSchema = z.object({
  data: z.object({
    environment: z.enum(['development', 'staging', 'production']),
    status: z.enum(['sent', 'replayed']),
    messageTs: z.string().min(1),
  }),
});

export type SyncWorkerSettingsValue = z.infer<typeof syncWorkerSettingsValueSchema>;
export type SyncWorkerSettingsMeta = z.infer<typeof syncWorkerSettingsMetaSchema>;
export type SyncWorkerSettingsEnvelope = z.infer<typeof syncWorkerSettingsEnvelopeSchema>;
export type SyncWorkerSettingsResponse = z.infer<typeof syncWorkerSettingsResponseSchema>;
export type SyncWorkerSlackReport = NonNullable<SyncWorkerSettingsResponse['slackReport']>;
export type SyncWorkerSlackReportResponse = z.infer<typeof syncWorkerSlackReportResponseSchema>;
